// ============================================
// UPDATED: SimpleHorizontalList.jsx
// Enhanced with rolling window focus preservation
// PERFORMANCE OPTIMIZED: Per-item memoization instead of whole-list memoization
// FIXED: Now reacts to isShiftingWindow changes to unlock scroll
// ============================================

import React, { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import { Box, CircularProgress, Typography, IconButton } from '@mui/material';
import { useLanguage } from '../../contexts/LanguageContext';
import PlaceDetails from '../PlaceDetails/PlaceDetails';
import { styled } from '@mui/system';
import axios from 'axios';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import SavedItemsService from '../../services/savedItemsService';

let useAuth;
try {
  const authModule = require('../Login/AuthContext');
  useAuth = authModule.useAuth;
} catch (e) {
  useAuth = () => ({ isAuthenticated: false });
}


// ============================================
// MEMOIZED ITEM COMPONENT
// ============================================

/**
 * Memoized horizontal event item - only re-renders when its props change
 * This is key to maintaining performance after removing whole-list memoization
 */
const HorizontalEventItem = React.memo(function HorizontalEventItem({
  place,
  index,
  isActive,
  onClick,
  language,
  onLocationClick,
  tracker,
  listId,
  onCreatorClick,
}) {
  const { isAuthenticated } = useAuth();
  const [isSaved, setIsSaved] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  const rawId = place?.event_id || place?.id;
  const isReview = place?._source === 'review';
  const isVenue = place?._source === 'venue';
  const savedItemType = isReview ? 'review' : isVenue ? 'venue' : 'event';
  const savedItemId = (() => {
    if (typeof rawId === 'string' && rawId.startsWith('review_')) return rawId.replace('review_', '');
    return String(parseInt(rawId, 10) || rawId);
  })();

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('token');
    return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !savedItemId || place?.__skeleton) return;
    SavedItemsService.getSaveStatus(savedItemId, savedItemType, getAuthHeaders())
      .then(({ saved }) => setIsSaved(saved))
      .catch(() => {});
  }, [isAuthenticated, savedItemId, savedItemType, getAuthHeaders, place]);

  const handleToggleSave = useCallback(async (e) => {
    e.stopPropagation();
    if (!isAuthenticated) {
      alert(language === 'TH' ? 'กรุณาเข้าสู่ระบบเพื่อบันทึกรายการ' : language === 'CN' ? '请登录以保存项目' : 'Please log in to save items');
      return;
    }
    if (saveLoading) return;
    setSaveLoading(true);
    try {
      const displayData = {
        item_name: place.event_name || place.name || place.place_name || '',
        item_image_url: place.image || place.image_url || place.event_image_link || place.main_image_url || '',
        item_location: place.event_location || place.location || place.address || '',
        item_date: place.event_date || place.created_at || '',
      };
      const { saved } = await SavedItemsService.toggleSave(savedItemId, savedItemType, displayData, getAuthHeaders());
      setIsSaved(saved);
    } catch (err) {
      console.error('[HorizontalEventItem] save error:', err);
    } finally {
      setSaveLoading(false);
    }
  }, [isAuthenticated, saveLoading, savedItemId, savedItemType, place, getAuthHeaders]);

  if (place?.__skeleton) {
    return (
      <ItemContainer data-index={index}>
        <div style={{
          width: '100%',
          height: '100%',
          borderRadius: 8,
          boxShadow: '0 1px 3px rgba(0,0,0,.12)',
          background: 'linear-gradient(90deg, #f2f2f2 25%, #e6e6e6 50%, #f2f2f2 75%)',
          backgroundSize: '200% 100%',
          animation: 'shimmer 1.5s infinite'
        }} />
      </ItemContainer>
    );
  }

  return (
    <ItemContainer
      data-event-id={place.id || place.event_id}
      data-index={index}
      onClick={() => onClick(place, index)}
    >
      <PlaceDetails
        place={place}
        selected={isActive}
        onLocationClick={onLocationClick}
        language={language}
        compact={true}
        onCreatorClick={onCreatorClick}
      />
      <IconButton
          size="small"
          onClick={handleToggleSave}
          disabled={saveLoading}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 10,
            bgcolor: 'rgba(0,0,0,0.45)',
            backdropFilter: 'blur(4px)',
            p: 0.6,
            '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' },
          }}
        >
          {isSaved
            ? <BookmarkIcon sx={{ fontSize: 18, color: '#a78bfa' }} />
            : <BookmarkBorderIcon sx={{ fontSize: 18, color: 'rgba(255,255,255,0.85)' }} />
          }
        </IconButton>
    </ItemContainer>
  );
});

const ScrollContainer = styled(Box)({
  height: '100%',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  overflow: 'hidden',
});

const HorizontalScrollBox = styled(Box)({
  flex: 1,
  overflowX: 'auto',
  overflowY: 'hidden',
  display: 'flex',
  alignItems: 'stretch',
  gap: '12px',
  padding: '10px 14px 14px',

  // INSTAGRAM-STYLE: Mobile optimizations
  WebkitOverflowScrolling: 'touch',
  overscrollBehavior: 'contain', // Prevent overscroll bounce
  scrollSnapType: 'x proximity',
  overflowAnchor: 'auto', // Browser-native scroll anchoring

  '&::-webkit-scrollbar': {
    height: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#888',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: '#555',
  },

  '@media (max-width: 600px)': {
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
});

const ItemContainer = styled(Box)({
  minWidth: '300px',
  maxWidth: '300px',
  width: '300px',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  scrollSnapAlign: 'start',
  flexShrink: 0,
  position: 'relative',

  '@media (max-width: 600px)': {
    minWidth: '54vw',
    maxWidth: '54vw',
    width: '54vw',
  },
});

const emptyMessages = {
  TH: 'ไม่พบกิจกรรม',
  EN: 'No events found matching your criteria.',
  CN: '未找到符合条件的活动'
};

// Time window to clamp focus after a shift (ms)
const POST_SHIFT_CLAMP_MS = 500; // Extended to ensure we don't miss the window

const SimpleHorizontalList = ({
  places,
  childClicked,
  isLoading,
  onLocationClick,
  onIndexChange,
  onActiveIndexChange,
  showPager = false,
  hasSearched = true,
  currentUser = null,
  listId = 'simple_horizontal_list_default',
  tracker = null,
  listRef: externalListRef = null,
  pendingScrollAdjustment = null,
  isShiftingWindow = false, // NEW: Prevent focus detection during window shifts
  isLoadingNext = false, // NEW: Show loading indicator when loading more items
  isLoadingPrevious = false, // NEW: For completeness (can add left-side indicator later)
  // NEW: Authoritative focus anchoring
  authoritativeFocusIndex = null,
  authoritativeFocusEventId = null,
  // NEW: Edge-demand channel - separate from focus for reliable loading
  onEdgeDemand = null,
  // NEW: Post-shift anchor - clamps focus for a short time after shift
  postShiftAnchorIndex = null,
  postShiftAnchorEventId = null,
  postShiftAnchorAt = null,
  onCreatorClick = null,
}) => {
  // Debug log to confirm re-renders
  console.log('[SimpleHorizontalList] render. isShiftingWindow =', isShiftingWindow);

  const { language } = useLanguage();
  const [currentIndex, setCurrentIndex] = useState(0);
  const scrollContainerRef = useRef(null);
  const isUserNavigatingRef = useRef(false);
  const lastChildClickedRef = useRef(null);
  const lastReportedIndexRef = useRef(-1);
  const previousItemCountRef = useRef(0);
  const scrollAdjustmentInProgressRef = useRef(false);
  const windowShiftCooldownRef = useRef(false); // NEW: Extended cooldown after window shifts
  const userScrollingRef = useRef(false); // NEW: Track if user is actively scrolling
  const preservedScrollPositionRef = useRef(null); // NEW: Preserve scroll during shifts
  const [isLocked, setIsLocked] = useState(false); // NEW: Lock scroll during critical shift period
  const isLockedRef = useRef(false); // Ref mirror of isLocked for native event handlers

  // Keep isLockedRef in sync with isLocked state
  useEffect(() => {
    isLockedRef.current = isLocked;
  }, [isLocked]);

  // STEP 4: cooldown to ignore auto-focus right after a window shift
  const postShiftFocusCooldownRef = useRef(false);
  const POST_SHIFT_FOCUS_TIMEOUT = 500; // ms, match the clamp duration

  // 🔍 DEBUG 1: Global wheel listener so we know wheel events are happening at all
  useEffect(() => {
    const handleGlobalWheel = (e) => {
      console.log('[GLOBAL wheel]', {
        deltaX: e.deltaX,
        deltaY: e.deltaY,
        targetTag: e.target?.tagName,
        targetClass: e.target?.className,
      });
    };

    window.addEventListener('wheel', handleGlobalWheel, { passive: true });
    console.log('[SimpleHorizontalList] 🧪 Global wheel debug listener attached');

    return () => {
      window.removeEventListener('wheel', handleGlobalWheel);
      console.log('[SimpleHorizontalList] 🧹 Global wheel debug listener removed');
    };
  }, []);

  // NEW: Authoritative focus anchoring - remember parent's focus so we don't overwrite it
  const authoritativeFocusRef = useRef(null);
  const lastScrollLeftRef = useRef(0);
  const USER_SCROLL_TOLERANCE_PX = 120;
  const USER_INDEX_TOLERANCE = 2;

  // NEW: Edge state tracking to prevent spamming edge demands
  const edgeStateRef = useRef(null); // "down" | "up" | null

  // NEW: Post-shift anchor clamp - prevents focus jitter for 280ms after shift
  const postShiftClampRef = useRef(null);

  // NEW: Dynamic item width measurement for accurate scroll compensation
  const itemWidthRef = useRef(320);

  // NEW: rAF-based scroll handler guard to prevent excessive firing
  const scrollRafRef = useRef(null);
  const disableSnapRef = useRef(false); // NEW: Track when to disable scroll-snap
  const dwellTimerRef = useRef(null);

  /**
   * Measure actual item width from first item for dynamic scroll compensation
   */
  const updateMeasuredWidth = useCallback((el) => {
    if (el && el.getBoundingClientRect) {
      const rect = el.getBoundingClientRect();
      if (rect.width > 0) {
        itemWidthRef.current = rect.width;
        console.log('[SimpleHorizontalList] 📐 Measured item width:', rect.width);
      }
    }
  }, []);

  /**
   * Native wheel listener for horizontal scrolling
   * Uses { passive: false } so preventDefault() works to stop page scroll
   */
  useEffect(() => {
    const container = scrollContainerRef.current;

    // 🔍 DEBUG: log every time the wheel effect runs
    console.log('[SimpleHorizontalList] 🧩 wheel effect run', {
      hasContainer: !!container,
      isLoading,
      isLoadingNext,
      isLoadingPrevious,
      placesLength: Array.isArray(places) ? places.length : -1,
    });

    if (!container) {
      console.log('[SimpleHorizontalList] ⚠️ wheel listener: no container yet in this render');
      return;
    }

    const wheelHandler = (event) => {
      // Use native event for more accurate debug info
      const native = event.nativeEvent || event;

      const {
        deltaX,
        deltaY,
        ctrlKey,
        shiftKey,
        altKey,
        metaKey,
        cancelable,
        defaultPrevented,
      } = native;

      // 🔹 NEW: mark user scroll immediately
      userScrollingRef.current = true;
      isUserNavigatingRef.current = false;

      // 🔹 NEW: Disable scroll-snap AND smooth scrolling during wheel
      disableSnapRef.current = true;
      container.style.scrollSnapType = 'none';
      container.style.scrollBehavior = 'auto'; // Disable smooth scroll for instant response

      // Basic debug log
      console.log('[SimpleHorizontalList] 🖱 native wheel', {
        deltaX,
        deltaY,
        ctrlKey,
        shiftKey,
        altKey,
        metaKey,
        cancelable,
        defaultPrevented,
        isLocked: isLockedRef.current,
        scrollLeftBefore: container.scrollLeft,
        scrollWidth: container.scrollWidth,
        clientWidth: container.clientWidth,
      });

      // If the list is locked (during window shift), do nothing and let the event bubble
      if (isLockedRef.current) {
        console.log('[SimpleHorizontalList] ⛔ native wheel: list is locked, ignoring');
        return;
      }

      // Ignore zoom and special gestures
      if (ctrlKey) {
        console.log('[SimpleHorizontalList] ℹ️ native wheel: ctrl pressed, ignoring');
        return;
      }

      // Only act if there is actually horizontal overflow
      const canScrollHorizontally = container.scrollWidth > container.clientWidth;
      if (!canScrollHorizontally) {
        console.log('[SimpleHorizontalList] ℹ️ native wheel: no horizontal overflow, ignoring');
        return;
      }

      // Only intercept when vertical motion is dominant (standard mouse wheel)
      if (Math.abs(deltaY) <= Math.abs(deltaX)) {
        console.log('[SimpleHorizontalList] ℹ️ native wheel: horizontal motion dominant, ignoring');
        return;
      }

      // Optional: safety check for coarse pointer (touch devices)
      if (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) {
        console.log('[SimpleHorizontalList] ℹ️ native wheel: coarse pointer detected, ignoring');
        return;
      }

      // We want to stop the page from scrolling vertically
      // and convert vertical wheel into horizontal movement.
      if (cancelable) {
        native.preventDefault();
      }
      native.stopPropagation();

      // Positive deltaY (wheel down) scrolls to the right, negative to the left
      const SCROLL_MULTIPLIER = 1; // adjust if you want faster/slower
      const scrollLeftBefore = container.scrollLeft;
      const nextScrollLeft = container.scrollLeft + deltaY * SCROLL_MULTIPLIER;

      console.log('[SimpleHorizontalList] 🧮 wheel calculation', {
        scrollLeftBefore,
        deltaY,
        multiplier: SCROLL_MULTIPLIER,
        nextScrollLeft,
        scrollWidth: container.scrollWidth,
        clientWidth: container.clientWidth,
        hasOverflow: container.scrollWidth > container.clientWidth,
      });

      container.scrollLeft = nextScrollLeft;

      // Read it back immediately to see if it stuck
      const scrollLeftAfter = container.scrollLeft;
      console.log('[SimpleHorizontalList] ✅ native wheel applied', {
        attempted: nextScrollLeft,
        actual: scrollLeftAfter,
        changed: scrollLeftAfter !== scrollLeftBefore,
      });

      // 🔹 Re-enable scroll-snap and smooth behavior after scrolling stops
      clearTimeout(window._wheelSnapReenableTimer);
      window._wheelSnapReenableTimer = setTimeout(() => {
        if (container && disableSnapRef.current) {
          container.style.scrollSnapType = 'x proximity';
          container.style.scrollBehavior = 'smooth';
          disableSnapRef.current = false;
          console.log('[SimpleHorizontalList] 🔄 Re-enabled scroll-snap and smooth behavior');
        }
      }, 300); // Increased to 300ms to ensure scroll has fully stopped
    };

    // Attach non-passive wheel listener so preventDefault is allowed
    container.addEventListener('wheel', wheelHandler, { passive: false });

    console.log('[SimpleHorizontalList] 🔗 native wheel listener attached', {
      scrollWidth: container.scrollWidth,
      clientWidth: container.clientWidth,
      canScrollHorizontally: container.scrollWidth > container.clientWidth,
    });

    return () => {
      container.removeEventListener('wheel', wheelHandler);
      clearTimeout(window._wheelSnapReenableTimer);
      console.log('[SimpleHorizontalList] 🔌 native wheel listener detached');
    };
  }, [
    // 🔁 Re-run this effect when loading state or data changes,
    // so we get a real container once the list exists
    isLoading,
    isLoadingNext,
    isLoadingPrevious,
    places,
  ]);

  const getEmptyMessage = () => {
    return emptyMessages[language] || emptyMessages.EN;
  };

  const validPlaces = useMemo(() => {
    return Array.isArray(places) ? places : [];
  }, [places]);

  useEffect(() => {
    return () => {
      tracker?.trackSessionEnd('horizontal_list_unmount');
      if (dwellTimerRef.current) clearTimeout(dwellTimerRef.current);
    };
  }, [tracker]);

  /**
   * STEP 2: Lock/unlock effect based on isShiftingWindow
   * STEP 4: Start cooldown when unlocking to prevent auto-focus from overriding preserved focus
   * This now works because we removed React.memo, so component re-renders when prop changes
   */
  useEffect(() => {
    if (isShiftingWindow) {
      setIsLocked(true);
      console.log('[SimpleHorizontalList] 🔒 Locking scroll (isShiftingWindow = true)');
    } else {
      setIsLocked(false);
      console.log('[SimpleHorizontalList] 🔓 Unlocking scroll (isShiftingWindow = false)');

      // STEP 4: start a short cooldown where we don't let auto-centering override hook's focus
      postShiftFocusCooldownRef.current = true;
      setTimeout(() => {
        postShiftFocusCooldownRef.current = false;
        console.log('[SimpleHorizontalList] ⏰ Post-shift focus cooldown ended');
      }, POST_SHIFT_FOCUS_TIMEOUT);
    }
  }, [isShiftingWindow, POST_SHIFT_FOCUS_TIMEOUT]);

  /**
   * NEW: Adopt authoritative focus from parent without reporting back
   * When parent tells us the focus, we reflect it in UI but don't echo it back
   */
  useEffect(() => {
    const count = Array.isArray(validPlaces) ? validPlaces.length : 0;
    if (
      typeof authoritativeFocusIndex === 'number' &&
      authoritativeFocusIndex >= 0 &&
      authoritativeFocusIndex < count
    ) {
      authoritativeFocusRef.current = {
        index: authoritativeFocusIndex,
        eventId: authoritativeFocusEventId ||
          validPlaces?.[authoritativeFocusIndex]?.id ||
          validPlaces?.[authoritativeFocusIndex]?.event_id ||
          null,
      };
      setCurrentIndex(authoritativeFocusIndex);
    }
  }, [authoritativeFocusIndex, authoritativeFocusEventId, validPlaces]);

  /**
   * NEW: Adopt post-shift anchor from parent
   * When parent tells us "I just shifted, this is the item", adopt it and clamp for 500ms
   * CRITICAL: Don't scroll - trust the removeTop compensation to keep position stable
   */
  useEffect(() => {
    if (
      typeof postShiftAnchorIndex === 'number' &&
      postShiftAnchorAt &&
      validPlaces[postShiftAnchorIndex]
    ) {
      // Record in ref for quick checks
      postShiftClampRef.current = {
        index: postShiftAnchorIndex,
        eventId: postShiftAnchorEventId,
        at: postShiftAnchorAt,
      };
      // Reflect in UI
      setCurrentIndex(postShiftAnchorIndex);
      console.log('[SimpleHorizontalList] 🧲 post-shift anchor adopted', {
        postShiftAnchorIndex,
        postShiftAnchorEventId,
      });

      // DON'T scroll into view - the removeTop compensation should keep position stable
      // Any additional scrolling causes visible movement (not like X.com)
      // The clamp will prevent focus from jumping for 500ms while position settles
    }
  }, [postShiftAnchorIndex, postShiftAnchorEventId, postShiftAnchorAt, validPlaces]);

  /**
   * IMPROVED: Two-way scroll compensation with support for both old and new shapes
   * Accepts both:
   * - Old shape: { type: 'removeTop', itemCount: 5 }
   * - New shape: { kind: 'removeTop', removedCount: 5 } or { kind: 'prepend', prependedCount: 5 }
   * Uses dynamic item width measurement for accuracy
   */
  useEffect(() => {
    if (!pendingScrollAdjustment || !scrollContainerRef.current) return;

    const container = scrollContainerRef.current;

    // Accept both old and new shapes
    const {
      type,
      itemCount,
      kind,
      removedCount,
      prependedCount,
    } = pendingScrollAdjustment;

    // Normalize type
    const finalType =
      type ||
      (kind === "removeTop"
        ? "removeTop"
        : kind === "prepend"
        ? "prepend"
        : null);

    // Normalize count
    const finalCount =
      typeof itemCount === "number"
        ? itemCount
        : typeof removedCount === "number"
        ? removedCount
        : typeof prependedCount === "number"
        ? prependedCount
        : 0;

    if (!finalType || finalCount <= 0) return;

    // Use measured item width (dynamic) with fallback to hardcoded value
    const ITEM_WIDTH = itemWidthRef.current;
    const GAP = 16;
    const totalWidth = finalCount * (ITEM_WIDTH + GAP);

    // Use rAF to avoid layout jank
    requestAnimationFrame(() => {
      if (finalType === "prepend") {
        // We added items to the left, so push scroll to the right
        container.scrollLeft = container.scrollLeft + totalWidth;
        console.log('[SimpleHorizontalList] 🟣 prepend adjust +', Math.round(totalWidth), 'px (', finalCount, 'items)');
      } else if (finalType === "removeTop") {
        // We removed items from the left, so pull scroll to the left
        const nextScroll = Math.max(0, container.scrollLeft - totalWidth);
        container.scrollLeft = nextScroll;
        console.log('[SimpleHorizontalList] 🟣 removeTop adjust -', Math.round(totalWidth), 'px (', finalCount, 'items)');
      }
    });
  }, [pendingScrollAdjustment]);

  const incrementItemViewCount = useCallback(async (item) => {
    const isReview = item._source === 'review';
    const id = parseInt(item.id, 10);
    const type = isReview ? 'review' : 'event';
    if (!id || isNaN(id)) return;
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/views/increment`, { type, id });
    } catch (err) {
      console.warn('[ViewCount] increment failed:', err.message);
    }
  }, []);

  /**
   * IMPROVED: Scroll handler with rAF optimization and better edge detection
   * Uses requestAnimationFrame to prevent excessive firing and improve performance
   */
  const handleScroll = useCallback((e) => {
    const container = e.target;

    // Guard: prevent multiple rAF calls
    if (scrollRafRef.current) return;

    scrollRafRef.current = requestAnimationFrame(() => {
      scrollRafRef.current = null;

      // Skip if navigating programmatically or during adjustment
      if (isUserNavigatingRef.current || scrollAdjustmentInProgressRef.current) {
        return;
      }

      // Mark that user is actively scrolling
      userScrollingRef.current = true;

      const scrollLeft = container.scrollLeft;
      const scrollWidth = container.scrollWidth;
      const clientWidth = container.clientWidth;

      // Use measured item width for accurate index calculation
      const itemWidth = itemWidthRef.current + 16; // width + gap
      const newIndex = Math.round(scrollLeft / itemWidth);
      const clampedIndex = Math.max(0, Math.min(validPlaces.length - 1, newIndex));

      // Only trigger focus/reporting if index changed and not a skeleton
      const hasChanged =
        clampedIndex !== currentIndex &&
        validPlaces[clampedIndex] &&
        !validPlaces[clampedIndex].__skeleton;

      // --- NEW: DEBUG edge checks (runs every scroll frame) ---
      const distanceToRight = scrollWidth - (scrollLeft + clientWidth);
      const THRESHOLD = 120; // horizontal cards are wide, allow some margin

      // Optional debug log to see edge calculations
      // (you can comment out later if too noisy)
      console.log('[SimpleHorizontalList] 🔍 edge check', {
        scrollLeft: Math.round(scrollLeft),
        scrollWidth: Math.round(scrollWidth),
        clientWidth: Math.round(clientWidth),
        distanceToRight: Math.round(distanceToRight),
        clampedIndex,
        totalItems: validPlaces.length,
        hasChanged,
        isShiftingWindow,
        windowShiftCooldown: windowShiftCooldownRef.current,
        edgeState: edgeStateRef.current,
      });

      // --- NEW: Edge demand detection separated from hasChanged ---
      if (
        typeof onEdgeDemand === 'function' &&
        !isShiftingWindow &&
        !windowShiftCooldownRef.current &&
        validPlaces.length > 0
      ) {
        const nearRightEdge =
          distanceToRight < THRESHOLD ||
          (typeof clampedIndex === 'number' &&
            clampedIndex >= Math.max(0, validPlaces.length - 3));

        if (nearRightEdge) {
          if (edgeStateRef.current !== 'down') {
            console.log(
              "[SimpleHorizontalList] edge reached → onEdgeDemand('down')",
              {
                distanceToRight: Math.round(distanceToRight),
                clampedIndex,
                totalItems: validPlaces.length,
              }
            );
            onEdgeDemand('down');
            edgeStateRef.current = 'down';
          }
        } else if (scrollLeft < 40) {
          if (edgeStateRef.current !== 'up') {
            console.log(
              '[SimpleHorizontalList] 🔔 Edge demand: UP',
              { scrollLeft: Math.round(scrollLeft) }
            );
            onEdgeDemand('up');
            edgeStateRef.current = 'up';
          }
        } else if (edgeStateRef.current !== null) {
          // Only clear when we're in the "middle zone"
          edgeStateRef.current = null;
        }
      }

      // --- Existing focus / reporting logic stays under hasChanged ---
      if (hasChanged) {
        setCurrentIndex(clampedIndex);

        const currentItem = validPlaces[clampedIndex];

        // Check if post-shift clamp is active
        const now = Date.now();
        const clamp = postShiftClampRef.current;
        const isClampActive =
          clamp && clamp.at && now - clamp.at < POST_SHIFT_CLAMP_MS;

        if (isClampActive) {
          if (currentIndex !== clamp.index) {
            setCurrentIndex(clamp.index);
          }
          console.log(
            '[SimpleHorizontalList] 🛑 clamping focus to post-shift anchor'
          );
        } else {
          // Normal path: Smart reporting with authoritative focus anchoring
          const anchor = authoritativeFocusRef.current;
          const currentScrollLeft = scrollLeft;
          lastScrollLeftRef.current = currentScrollLeft;

          let canReport = true;

          // 1) Still in cooldown → don't report
          if (postShiftFocusCooldownRef.current) {
            console.log(
              '[SimpleHorizontalList] ⏸️  Skipping focus report during post-shift cooldown'
            );
            canReport = false;
          }

          // 2) If parent has an anchor, don't override unless user moved far enough
          if (canReport && anchor) {
            const indexDiff = Math.abs(clampedIndex - anchor.index);
            const scrollDiff = Math.abs(
              currentScrollLeft - (lastScrollLeftRef.current || 0)
            );
            if (
              indexDiff <= USER_INDEX_TOLERANCE &&
              scrollDiff <= USER_SCROLL_TOLERANCE_PX
            ) {
              canReport = false;
            }
          }

          // 3) Finally, report
          if (canReport && currentItem && !currentItem.__skeleton) {
            if (onIndexChange) {
              onIndexChange(clampedIndex, currentItem);
            }
            if (onActiveIndexChange) {
              onActiveIndexChange(clampedIndex, currentItem);
            }
          }
        }

        // Start 3-second dwell timer for the newly centered item
        if (dwellTimerRef.current) {
          clearTimeout(dwellTimerRef.current);
          dwellTimerRef.current = null;
        }
        const centeredItem = validPlaces[clampedIndex];
        if (centeredItem && !centeredItem.__skeleton) {
          dwellTimerRef.current = setTimeout(() => {
            dwellTimerRef.current = null;
            incrementItemViewCount(centeredItem);
          }, 3000);
        }
      }

      // Clear user scrolling flag after inactivity
      clearTimeout(window._horizontalScrollInactivityTimer);
      window._horizontalScrollInactivityTimer = setTimeout(() => {
        userScrollingRef.current = false;
      }, 200);
    });
  }, [
    currentIndex,
    validPlaces,
    onIndexChange,
    onActiveIndexChange,
    tracker,
    isShiftingWindow,
    onEdgeDemand,
    incrementItemViewCount,
  ]);

  /**
   * Set up scroll listener with passive flag for better performance
   * The handleScroll function now uses rAF internally, so we can call it directly
   */
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll, { passive: true });

      return () => {
        container.removeEventListener('scroll', handleScroll);
        // Clean up any pending rAF
        if (scrollRafRef.current) {
          cancelAnimationFrame(scrollRafRef.current);
          scrollRafRef.current = null;
        }
      };
    }
  }, [handleScroll]);


  /**
   * Auto-scroll when item clicked from map
   * ENHANCED: Respects window shifting + cooldown + user scrolling
   */
  useEffect(() => {
    const container = scrollContainerRef.current;

    console.log('[SimpleHorizontalList] 🔍 auto-scroll effect check', {
      hasContainer: !!container,
      childClicked,
      currentIndex,
      isShiftingWindow,
      windowShiftCooldown: windowShiftCooldownRef.current,
      userScrolling: userScrollingRef.current,
      scrollAdjustment: scrollAdjustmentInProgressRef.current,
    });

    if (!container ||
        !Array.isArray(validPlaces) ||
        childClicked == null ||
        childClicked < 0 ||
        childClicked >= validPlaces.length ||
        scrollAdjustmentInProgressRef.current ||
        isShiftingWindow ||
        windowShiftCooldownRef.current ||
        userScrollingRef.current) {
      console.log('[SimpleHorizontalList] ⏭ auto-scroll effect: guard hit, skipping scrollToIndex');
      return;
    }

    console.log('[SimpleHorizontalList] 🚀 auto-scroll effect: calling scrollToIndex', {
      childClicked,
      scrollLeftBefore: container.scrollLeft,
    });

    scrollToIndex(childClicked);
    lastChildClickedRef.current = childClicked;
  }, [childClicked, currentIndex, isShiftingWindow, validPlaces]);

  /**
   * Detect when isShiftingWindow changes - LOCK during shift
   * NOTE: We DON'T preserve scroll position here - let removeTop compensation handle it
   */
  useEffect(() => {
    if (isShiftingWindow) {
      windowShiftCooldownRef.current = true;
      setIsLocked(true);
      console.log('[HorizontalList] 🔒 Window shift started - LOCKING (no scroll preservation)');
    } else {
      setIsLocked(false);
      console.log('[HorizontalList] 🔓 Shift done - unlocking immediately');

      // keep a cooldown where we ignore edge-demand
      windowShiftCooldownRef.current = true;
      setTimeout(() => {
        windowShiftCooldownRef.current = false;
      }, 400); // 400ms cooldown to prevent edge demand right after shift
    }
  }, [isShiftingWindow]);

  /**
   * NOTE: We removed scroll position preservation during shifts
   * The removeTop compensation is sufficient and preserving scroll was fighting it
   */

  const scrollToIndex = useCallback((index) => {
    if (!scrollContainerRef.current || index < 0 || index >= validPlaces.length) return;

    const container = scrollContainerRef.current;

    console.log('[SimpleHorizontalList] 🎯 scrollToIndex called', {
      index,
      currentScrollLeft: container.scrollLeft,
      itemWidth: itemWidthRef.current,
    });

    isUserNavigatingRef.current = true;

    const itemWidth = itemWidthRef.current + 16;
    const containerWidth = container.clientWidth;
    const targetScrollLeft = index * itemWidth - (containerWidth / 2) + (itemWidthRef.current / 2);

    console.log('[SimpleHorizontalList] 🎯 scrollToIndex target', {
      index,
      targetScrollLeft,
    });

    container.scrollTo({
      left: targetScrollLeft,
      behavior: 'smooth'
    });

    setCurrentIndex(index);

    setTimeout(() => {
      isUserNavigatingRef.current = false;
    }, 800);
  }, [validPlaces.length]);

  const handleDotClick = useCallback((index) => {
    scrollToIndex(index);

    // TASK 5: Pass item to callbacks
    const currentItem = validPlaces[index];
    if (onIndexChange) {
      onIndexChange(index, currentItem);
    }
    if (onActiveIndexChange) {
      onActiveIndexChange(index, currentItem);
    }
  }, [scrollToIndex, validPlaces, onIndexChange, onActiveIndexChange]);

  const handleItemClick = useCallback((place, index) => {
    if (place?.__skeleton) return;

    const eventId = place.id || place.event_id;

    if (tracker) {
      tracker.trackInteraction({
        eventId: eventId,
        action: 'click_event_card',
        context: 'horizontal_list',
        position: index,
        timestamp: Date.now(),
        metadata: {
          listId: listId,
          layout: 'horizontal',
          source: 'simple_horizontal_list'
        }
      });

      const rawBag = [
        place?.name || '',
        place?.category || '',
        place?.sub_category || '',
        Array.isArray(place?.tags) ? place.tags.join(' ') : ''
      ].join(' ');

      if (rawBag.trim().length > 0) {
        tracker.trackKeywordInterest(rawBag, {
          source: 'event_click',
          eventId: eventId,
          weight: 0.6,
          lang: language
        });
      }
    }

    // Center scroll card in the horizontal list
    scrollToIndex(index);

    // Notify parent to highlight marker, open popup, and pan map camera
    if (onIndexChange) {
      onIndexChange(index, place);
    }
    if (onActiveIndexChange) {
      onActiveIndexChange(index, place);
    }
  }, [tracker, listId, language, scrollToIndex, onIndexChange, onActiveIndexChange]);

  /**
   * Extra scroll state logging - render snapshot
   */
  useLayoutEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      console.log('[SimpleHorizontalList] 🧱 render snapshot', {
        scrollLeft: container.scrollLeft,
        scrollWidth: container.scrollWidth,
        clientWidth: container.clientWidth,
      });
    }
  }, [validPlaces.length, childClicked, currentIndex]);

  if (isLoading) {
    return (
      <ScrollContainer>
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%'
        }}>
          <CircularProgress size={50} />
        </Box>
      </ScrollContainer>
    );
  }

  if (validPlaces.length === 0) {
    return (
      <ScrollContainer>
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%'
        }}>
          <Typography>
            {hasSearched ? getEmptyMessage() : (
              language === 'TH' ? 'กำลังโหลด...' :
              language === 'CN' ? '加载中...' :
              'Loading...'
            )}
          </Typography>
        </Box>
      </ScrollContainer>
    );
  }

  return (
    <ScrollContainer sx={{ position: 'relative' }}>
      {showPager && (
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          py: 1,
          px: 2,
          backgroundColor: '#f8f8f8',
          borderBottom: '1px solid #e0e0e0'
        }}>
          <Typography variant="caption" color="text.secondary">
            {currentIndex + 1} / {validPlaces.length}
          </Typography>
        </Box>
      )}

      {/* Loading indicator at right edge */}
      {isLoadingNext && (
        <Box sx={{
          position: 'absolute',
          right: 16,
          top: '50%',
          transform: 'translateY(-50%)',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          color: 'white',
          px: 2,
          py: 1,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          zIndex: 1000,
          pointerEvents: 'none',
        }}>
          <CircularProgress size={16} color="inherit" />
          <Typography sx={{ fontSize: '0.875rem' }}>Loading...</Typography>
        </Box>
      )}

      <HorizontalScrollBox
        ref={scrollContainerRef}
        className="horizontal-rail"
        sx={{
          touchAction: 'pan-x',
          scrollBehavior: isLocked ? 'auto' : 'smooth',
          overflowX: isLocked ? 'hidden' : 'auto',
          pointerEvents: isLocked ? 'none' : 'auto',
          paddingRight: '32px', // Extra padding for smoother edge loading
        }}
      >
        {validPlaces.map((place, index) => {
          // Add measurement ref to first non-skeleton item
          const shouldMeasure = index === 0 && place && !place.__skeleton;

          return (
            <div
              key={place.event_id || place.id || `place-${index}`}
              ref={shouldMeasure ? updateMeasuredWidth : null}
              style={{ display: 'contents' }}
            >
              <HorizontalEventItem
                place={place}
                index={index}
                isActive={index === childClicked}
                onClick={handleItemClick}
                language={language}
                onLocationClick={onLocationClick}
                tracker={tracker}
                listId={listId}
                onCreatorClick={onCreatorClick}
              />
            </div>
          );
        })}
      </HorizontalScrollBox>

      {showPager && validPlaces.length > 1 && (
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          py: 1,
          gap: 0.5
        }}>
          {validPlaces.slice(
            Math.max(0, currentIndex - 2),
            Math.min(validPlaces.length, currentIndex + 3)
          ).map((_, idx) => {
            const actualIndex = Math.max(0, currentIndex - 2) + idx;
            return (
              <Box
                key={actualIndex}
                onClick={() => handleDotClick(actualIndex)}
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  backgroundColor: actualIndex === currentIndex ? '#1976d2' : '#e0e0e0',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s ease'
                }}
              />
            );
          })}
        </Box>
      )}

      <style>{`
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
    </ScrollContainer>
  );
};

// STEP 1: Remove outer React.memo - export component directly
// This allows the component to re-render when isShiftingWindow changes
export default SimpleHorizontalList;
